import React, { useEffect, useRef } from 'react';
import '../Blog/Blog.css';
import { Link } from 'react-router-dom';
import Blog1 from '../../Assets/blog/blog1.jpg';
import Blog2 from '../../Assets/blog/blog2.jpg';
import Blog3 from '../../Assets/blog/blog3.jpg';
import Blog4 from '../../Assets/blog/blog4.jpg';
import Blog5 from '../../Assets/blog/blog5.jpg';
import Blog6 from '../../Assets/blog/blog6.jpg';

const articles = [
    {
        id: 1,
        title: "The Three Levels of Audits: This Guide Reveals Which You Need",
        date: "October 21, 2021",
        category: "Auditing",
        readTime: "5 min read",
        description: "So, you've made the commendable decision to start an independent audit on your business. This should be a simple process that puts you in an incredibly advantageous position.",
        image: Blog1,
    },
    {
        id: 2,
        title: "Why Would You Need an Independent Audit? What Can You Get Out of It?",
        date: "October 21, 2021",
        category: "Auditing",
        readTime: "4 min read",
        description: "Business owners typically aren't thrilled to be audited. But, genuinely, audits can be a welcomed, useful tool that establishes confidence in your business — from you.",
        image: Blog2,
    },
    {
        id: 3,
        title: "Your Guide to Optimizing Charitable Donations with Canadian Tax Credits",
        date: "October 21, 2021",
        category: "Tax Credits",
        readTime: "6 min read",
        description: "Did you know the average age of Canadian donors hovers around 55? Those approaching retirement are giving away $3,300 — here's how to maximize every dollar.",
        image: Blog3,
    },
    {
        id: 4,
        title: "How to Deduct Charitable Donations from Your Brampton Small Business",
        date: "October 21, 2021",
        category: "Small Business",
        readTime: "5 min read",
        description: "Nearly a quarter of Canadians donated to charity because of the tax credit. Learn how to save on taxes while supporting causes that matter to you.",
        image: Blog4,
    },
    {
        id: 5,
        title: "Create a Small Business Financial Plan by Starting Here",
        date: "October 21, 2021",
        category: "Financial Planning",
        readTime: "7 min read",
        description: "A solid financial plan is the backbone of every thriving small business. Discover where to start and what pillars to build your plan around.",
        image: Blog5,
    },
    {
        id: 6,
        title: "How Do CRA Audits Work? What Do You Need to Prepare?",
        date: "October 21, 2021",
        category: "CRA & Compliance",
        readTime: "6 min read",
        description: "Understanding the CRA audit process can save you time, stress, and money. Here's exactly what to expect and how to prepare your documents.",
        image: Blog6,
    },
];

/* ── Arrow icon ─────────────────────────────── */
const ArrowIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
);

/* ── Calendar icon ───────────────────────────── */
const CalIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
);

/* ── Clock icon ──────────────────────────────── */
const ClockIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
    </svg>
);

/* ════════════════════════════════════════════════
   FEATURED card (first article — full-width)
   ════════════════════════════════════════════════ */
const FeaturedCard = ({ article, innerRef }) => (
    <div className="bp-featured" ref={innerRef}>
        <div className="bp-featured__img-wrap">
            <img src={article.image} alt={article.title} className="bp-featured__img" />
            <div className="bp-featured__overlay" />
            <span className="bp-featured__badge">Featured</span>
        </div>
        <div className="bp-featured__body">
            <span className="bp-tag">{article.category}</span>
            <h2 className="bp-featured__title">{article.title}</h2>
            <p className="bp-featured__desc">{article.description}</p>
            <div className="bp-meta">
                <CalIcon /><span>{article.date}</span>
                <ClockIcon /><span>{article.readTime}</span>
            </div>
            <Link to={`/blogdetails/${article.id}`} className="bp-btn">
                Read Article <ArrowIcon />
            </Link>
        </div>
    </div>
);

/* ════════════════════════════════════════════════
   REGULAR card
   ════════════════════════════════════════════════ */
const RegularCard = ({ article, innerRef, delay }) => (
    <div
        className="bp-card"
        ref={innerRef}
        style={{ '--delay': delay }}
    >
        <div className="bp-card__img-wrap">
            <img src={article.image} alt={article.title} className="bp-card__img" />
            <div className="bp-card__overlay" />
            <span className="bp-tag bp-tag--img">{article.category}</span>
        </div>
        <div className="bp-card__body">
            <div className="bp-meta bp-meta--top">
                <CalIcon /><span>{article.date}</span>
                <ClockIcon /><span>{article.readTime}</span>
            </div>
            <h3 className="bp-card__title">{article.title}</h3>
            <p className="bp-card__desc">{article.description}</p>
            <Link to={`/blogdetails/${article.id}`} className="bp-card__link">
                Read More <ArrowIcon />
            </Link>
        </div>
        <div className="bp-card__bar" />
    </div>
);

/* ════════════════════════════════════════════════
   MAIN component
   ════════════════════════════════════════════════ */
const BlogPost = () => {
    const featuredRef = useRef(null);
    const cardRefs = useRef([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('bp--visible');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.1 }
        );

        if (featuredRef.current) observer.observe(featuredRef.current);
        cardRefs.current.forEach((el) => { if (el) observer.observe(el); });

        return () => observer.disconnect();
    }, []);

    const [featured, ...rest] = articles;

    return (
        <div className="bp-wrap container-xxl">
            {/* Featured post */}
            <FeaturedCard article={featured} innerRef={featuredRef} />

            {/* Grid of remaining posts */}
            <div className="bp-grid">
                {rest.map((article, idx) => (
                    <RegularCard
                        key={article.id}
                        article={article}
                        innerRef={(el) => (cardRefs.current[idx] = el)}
                        delay={`${idx * 100}ms`}
                    />
                ))}
            </div>
        </div>
    );
};

export default BlogPost;